import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  fetchCurriculum,
  fetchCourseById,
  addLesson,
  updateLesson,
  deleteLesson,
  reorderCurriculum,
} from "./adminCourseService";
import StudyMaterialsPanel from "./StudyMaterialsPanel";
import "./adminCourses.css";

/* ── blank lesson template ── */
const BLANK = {
  dayNumber: "",
  sortOrder: "",
  title: "",
  learningObjective: "",
  durationMins: 30,
  isAssessment: false,
};

/* ─────────────────────────────────────────────
   Sortable row — one lesson
───────────────────────────────────────────── */
const LessonRow = ({ lesson, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: lesson.id });
  const [showMaterials, setShowMaterials] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "#eff6ff" : undefined,
  };

  return (
    <>
      <tr ref={setNodeRef} style={style}>
        <td
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", textAlign: "center", color: "#9ca3af", userSelect: "none" }}
          title="Drag to reorder"
        >
          ⠿
        </td>
        <td>{lesson.dayNumber}</td>
        <td>{lesson.sortOrder}</td>
        <td>
          {lesson.title}
          {lesson.isAssessment && (
            <span className="ac-badge ac-badge-purple" style={{ marginLeft: "0.4rem" }}>
              Assessment
            </span>
          )}
        </td>
        <td style={{ maxWidth: 220, fontSize: "0.82rem", color: "#6b7280" }}>
          {lesson.learningObjective || "—"}
        </td>
        <td>{lesson.durationMins} min</td>
        <td>
          <div className="ac-actions">
            <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => setShowMaterials((p) => !p)}>
              📚 Materials
            </button>
            <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => onEdit(lesson)}>
              Edit
            </button>
            <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => onDelete(lesson)}>
              Delete
            </button>
          </div>
        </td>
      </tr>
      {showMaterials && (
        <tr>
          <td colSpan={7} style={{ padding: "0 1rem 1rem 3rem", background: "#fafafa" }}>
            <StudyMaterialsPanel curriculumId={lesson.id} />
          </td>
        </tr>
      )}
    </>
  );
};

/* ─────────────────────────────────────────────
   Inline lesson form (add / edit)
───────────────────────────────────────────── */
const LessonForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial || BLANK);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      dayNumber: Number(f.dayNumber),
      sortOrder: Number(f.sortOrder),
      title: f.title,
      learningObjective: f.learningObjective,
      durationMins: Number(f.durationMins),
      isAssessment: f.isAssessment,
    });
  };

  return (
    <tr style={{ background: "#f0f9ff" }}>
      <td />
      <td>
        <input
          type="number" min={1} required
          value={f.dayNumber}
          onChange={(e) => set("dayNumber", e.target.value)}
          style={{ width: 60, padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <input
          type="number" min={1} required
          value={f.sortOrder}
          onChange={(e) => set("sortOrder", e.target.value)}
          style={{ width: 60, padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <input
          required
          value={f.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="Lesson title"
          style={{ width: "100%", padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <input
          value={f.learningObjective}
          onChange={(e) => set("learningObjective", e.target.value)}
          placeholder="Objective"
          style={{ width: "100%", padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <input
          type="number" min={1}
          value={f.durationMins}
          onChange={(e) => set("durationMins", e.target.value)}
          style={{ width: 60, padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.82rem" }}>
            <input
              type="checkbox"
              checked={f.isAssessment}
              onChange={(e) => set("isAssessment", e.target.checked)}
            />
            Assessment
          </label>
          <button
            type="button"
            className="ac-btn ac-btn-success ac-btn-sm"
            disabled={saving}
            onClick={handleSubmit}
          >
            {saving ? "…" : "Save"}
          </button>
          <button type="button" className="ac-btn ac-btn-ghost ac-btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
};

/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const AdminCurriculumPage = () => {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [courseName, setCourseName] = useState("");
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLesson, setEditingLesson] = useState(null); // lesson obj or null
  const [addingNew, setAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const sensors = useSensors(useSensor(PointerSensor));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, currRes] = await Promise.all([
        fetchCourseById(courseId),
        fetchCurriculum(courseId),
      ]);
      setCourseName(courseRes.data?.name || "");
      const sorted = (currRes.data || []).sort((a, b) => a.sortOrder - b.sortOrder);
      setLessons(sorted);
    } catch {
      setError("Failed to load curriculum.");
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  /* ── drag end → reorder ── */
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = lessons.findIndex((l) => l.id === active.id);
    const newIndex = lessons.findIndex((l) => l.id === over.id);
    const reordered = arrayMove(lessons, oldIndex, newIndex);
    setLessons(reordered);

    try {
      await reorderCurriculum(courseId, reordered.map((l) => l.id));
    } catch {
      setError("Reorder failed. Refreshing…");
      load();
    }
  };

  /* ── add lesson ── */
  const handleAdd = async (data) => {
    setSaving(true);
    setError("");
    try {
      await addLesson(courseId, data);
      setAddingNew(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Save failed.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  /* ── update lesson ── */
  const handleUpdate = async (data) => {
    setSaving(true);
    setError("");
    try {
      await updateLesson(courseId, editingLesson.id, data);
      setEditingLesson(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Update failed.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  /* ── delete lesson ── */
  const handleDelete = async (lesson) => {
    if (!window.confirm(`Delete Day ${lesson.dayNumber}: "${lesson.title}"?`)) return;
    setError("");
    try {
      await deleteLesson(courseId, lesson.id);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Delete failed.";
      setError(String(msg));
    }
  };

  if (loading) return <div className="ac-loading">Loading curriculum…</div>;

  return (
    <div>
      <div className="ac-header">
        <div>
          <h2>Curriculum</h2>
          <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "0.2rem" }}>
            {courseName} — {lessons.length} lesson{lessons.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="ac-actions">
          <button
            className="ac-btn ac-btn-primary"
            onClick={() => { setAddingNew(true); setEditingLesson(null); }}
            disabled={addingNew}
          >
            + Add Lesson
          </button>
          <button className="ac-btn ac-btn-ghost" onClick={() => navigate(`/admin/courses`)}>
            ← Courses
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: "6px",
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      <div className="ac-table-wrap">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={lessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
            <table className="ac-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }} />
                  <th>Day</th>
                  <th>Order</th>
                  <th>Title</th>
                  <th>Learning Objective</th>
                  <th>Duration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Add new row at top */}
                {addingNew && (
                  <LessonForm
                    onSave={handleAdd}
                    onCancel={() => setAddingNew(false)}
                    saving={saving}
                  />
                )}

                {lessons.length === 0 && !addingNew && (
                  <tr>
                    <td colSpan={7} className="ac-empty">
                      No lessons yet. Click "+ Add Lesson" to start building the curriculum.
                    </td>
                  </tr>
                )}

                {lessons.map((lesson) =>
                  editingLesson?.id === lesson.id ? (
                    <LessonForm
                      key={lesson.id}
                      initial={editingLesson}
                      onSave={handleUpdate}
                      onCancel={() => setEditingLesson(null)}
                      saving={saving}
                    />
                  ) : (
                    <LessonRow
                      key={lesson.id}
                      lesson={lesson}
                      onEdit={(l) => { setEditingLesson(l); setAddingNew(false); }}
                      onDelete={handleDelete}
                    />
                  )
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>

      <div style={{ marginTop: "0.75rem", fontSize: "0.8rem", color: "#9ca3af" }}>
        Drag rows to reorder. Day 1 and Day 21 should have the Assessment flag checked.
        Changes only affect future batches.
      </div>
    </div>
  );
};

export default AdminCurriculumPage;
